<template>
  <div class="regex-tester">
    <h1>正则表达式测试工具</h1>
    
    <div class="tester-container">
      <!-- 正则表达式输入 -->
      <div class="section">
        <h3>正则表达式</h3>
        <div class="regex-input">
          <span class="delimiter">/</span>
          <el-input
            v-model="pattern"
            placeholder="输入正则表达式"
            @input="handleTest"
          />
          <span class="delimiter">/</span>
          <el-input
            v-model="flags"
            class="flags-input"
            placeholder="flags"
            @input="handleTest"
          />
        </div>
      </div>

      <!-- 标志位说明 -->
      <div class="flags-info">
        <el-tag v-if="flags.includes('g')" class="flag-tag">g - 全局匹配</el-tag>
        <el-tag v-if="flags.includes('i')" class="flag-tag">i - 忽略大小写</el-tag>
        <el-tag v-if="flags.includes('m')" class="flag-tag">m - 多行匹配</el-tag>
        <el-tag v-if="flags.includes('s')" class="flag-tag">s - 允许 . 匹配换行符</el-tag>
        <el-tag v-if="flags.includes('u')" class="flag-tag">u - Unicode 模式</el-tag>
        <el-tag v-if="flags.includes('y')" class="flag-tag">y - 粘性匹配</el-tag>
      </div>

      <!-- 测试文本输入 -->
      <div class="section">
        <h3>测试文本</h3>
        <el-input
          v-model="testText"
          type="textarea"
          :rows="8"
          placeholder="输入要测试的文本"
          @input="handleTest"
        />
      </div>

      <!-- 匹配结果 -->
      <div class="section">
        <h3>匹配结果</h3>
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
        <template v-else>
          <div class="match-info" v-if="matches.length > 0">
            找到 {{ matches.length }} 个匹配项
          </div>
          <div class="match-list" v-if="matches.length > 0">
            <div v-for="(match, index) in matches" :key="index" class="match-item">
              <div class="match-header">
                匹配项 #{{ index + 1 }}
              </div>
              <div class="match-content">
                <div>完整匹配: <span class="match-text">{{ match[0] }}</span></div>
                <template v-if="match.length > 1">
                  <div v-for="(group, groupIndex) in match.slice(1)" :key="groupIndex">
                    分组 {{ groupIndex + 1 }}: <span class="match-text">{{ group }}</span>
                  </div>
                </template>
                <div>位置: {{ match.index }}</div>
              </div>
            </div>
          </div>
          <div v-else class="no-matches">
            没有找到匹配项
          </div>
        </template>
      </div>

      <!-- 常用正则表达式 -->
      <div class="section">
        <h3>常用正则表达式</h3>
        <div class="common-patterns">
          <el-button
            v-for="item in commonPatterns"
            :key="item.name"
            @click="useCommonPattern(item)"
          >
            {{ item.name }}
          </el-button>
        </div>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>使用说明</h3>
        <ul>
          <li>在正则表达式输入框中输入表达式，无需添加开始和结束的斜杠</li>
          <li>在 flags 输入框中添加标志位（如：g, i, m 等）</li>
          <li>支持正则表达式分组捕获</li>
          <li>点击常用正则表达式可快速使用</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'

const pattern = ref('')
const flags = ref('g')
const testText = ref('')
const matches = ref([])
const error = ref('')

const commonPatterns = [
  {
    name: '手机号码',
    pattern: '^1[3-9]\\d{9}$',
    description: '匹配中国大陆手机号码',
    example: '13812345678'
  },
  {
    name: '电子邮箱',
    pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
    description: '匹配电子邮箱地址',
    example: 'example@email.com'
  },
  {
    name: '身份证号',
    pattern: '^[1-9]\\d{5}(19|20)\\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\\d|3[01])\\d{3}[0-9Xx]$',
    description: '匹配18位身份证号码',
    example: '440101199001011234'
  },
  {
    name: 'URL地址',
    pattern: '^(https?:\\/\\/)?([\\da-z.-]+)\\.([a-z.]{2,6})([\\/\\w .-]*)*\\/?$',
    description: '匹配网址URL',
    example: 'https://www.example.com'
  },
  {
    name: 'IPv4地址',
    pattern: '^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$',
    description: '匹配IPv4地址',
    example: '192.168.1.1'
  },
  {
    name: '日期格式(yyyy-mm-dd)',
    pattern: '^\\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])$',
    description: '匹配日期格式 YYYY-MM-DD',
    example: '2023-12-31'
  },
  {
    name: '时间格式(HH:mm:ss)',
    pattern: '^([01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d$',
    description: '匹配时间格式 HH:MM:SS',
    example: '23:59:59'
  },
  {
    name: '中文字符',
    pattern: '^[\\u4e00-\\u9fa5]+$',
    description: '匹配中文字符',
    example: '中文汉字'
  },
  {
    name: '密码强度',
    pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$',
    description: '至少8位，包含大小写字母、数字和特殊字符',
    example: 'Test123!@#'
  },
  {
    name: '用户名',
    pattern: '^[a-zA-Z0-9_-]{4,16}$',
    description: '4-16位字母、数字、下划线、减号',
    example: 'user_name123'
  },
  {
    name: '16进制颜色',
    pattern: '^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$',
    description: '匹配16进制颜色值',
    example: '#FF0000'
  },
  {
    name: '邮政编码',
    pattern: '^[1-9]\\d{5}$',
    description: '匹配中国邮政编码',
    example: '100000'
  },
  {
    name: 'QQ号码',
    pattern: '^[1-9][0-9]{4,10}$',
    description: '匹配QQ号码',
    example: '10000'
  },
  {
    name: '车牌号',
    pattern: '^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领][A-Z][A-HJ-NP-Z0-9]{4,5}[A-HJ-NP-Z0-9挂学警港澳]$',
    description: '匹配中国车牌号',
    example: '京A12345'
  },
  {
    name: '版本号',
    pattern: '^\\d+\\.\\d+\\.\\d+$',
    description: '匹配版本号格式',
    example: '1.2.3'
  },
  {
    name: '整数',
    pattern: '^-?\\d+$',
    description: '匹配整数（正数、负数、0）',
    example: '-123'
  },
  {
    name: '小数',
    pattern: '^-?\\d+\\.\\d+$',
    description: '匹配小数',
    example: '3.14'
  },
  {
    name: '金额',
    pattern: '^\\d+\\.\\d{2}$',
    description: '匹配金额（两位小数）',
    example: '99.99'
  },
  {
    name: '中国固话',
    pattern: '^(\\d{3,4}-)?\\d{7,8}$',
    description: '匹配中国固定电话',
    example: '010-12345678'
  },
  {
    name: '统一社会信用代码',
    pattern: '^[0-9A-HJ-NPQRTUWXY]{2}\\d{6}[0-9A-HJ-NPQRTUWXY]{10}$',
    description: '匹配统一社会信用代码',
    example: '91110108MA0123456789'
  },
  {
    name: 'MAC地址',
    pattern: '^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$',
    description: '匹配MAC地址',
    example: '00:11:22:33:44:55'
  },
  {
    name: '文件路径',
    pattern: '^(/[^/ ]*)+/?$',
    description: '匹配Linux文件路径',
    example: '/usr/local/bin'
  },
  {
    name: 'Windows文件路径',
    pattern: '^[a-zA-Z]:\\\\(?:[^\\\\/:*?"<>|\\r\\n]+\\\\)*[^\\\\/:*?"<>|\\r\\n]*$',
    description: '匹配Windows文件路径',
    example: 'C:\\Windows\\System32'
  },
  {
    name: '护照号码',
    pattern: '^[A-Za-z0-9]{6,9}$',
    description: '匹配护照号码',
    example: 'E12345678'
  },
  {
    name: '强密码',
    pattern: '^.*(?=.{8,})(?=.*\\d)(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*? ]).*$',
    description: '至少8位，包含大小写字母、数字和特殊字符',
    example: 'Test123!@#'
  },
  {
    name: '银行卡号',
    pattern: '^[1-9]\\d{9,29}$',
    description: '匹配银行卡号（10-30位数字）',
    example: '6222021234567890123'
  },
  {
    name: '中文姓名',
    pattern: '^[\\u4e00-\\u9fa5]{2,4}$',
    description: '匹配中文姓名（2-4个汉字）',
    example: '张三'
  },
  {
    name: '英文姓名',
    pattern: '^[a-zA-Z]{2,}\\s[a-zA-Z]{1,}\\s?([a-zA-Z]{1,})?$',
    description: '匹配英文姓名（支持中间名）',
    example: 'John Smith'
  },
  {
    name: '域名',
    pattern: '^([a-zA-Z0-9]([a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,6}$',
    description: '匹配域名',
    example: 'example.com'
  },
  {
    name: 'IPv6地址',
    pattern: '^([\\da-fA-F]{1,4}:){7}[\\da-fA-F]{1,4}$',
    description: '匹配IPv6地址',
    example: '2001:0db8:85a3:0000:0000:8a2e:0370:7334'
  },
  {
    name: '经度',
    pattern: '^[-+]?(180(\\.0+)?|((1[0-7]\\d)|([1-9]?\\d))(\\.\\d+)?)$',
    description: '匹配经度值（-180到180）',
    example: '120.5'
  },
  {
    name: '纬度',
    pattern: '^[-+]?([1-8]?\\d(\\.\\d+)?|90(\\.0+)?)$',
    description: '匹配纬度值（-90到90）',
    example: '23.5'
  },
  {
    name: 'IMEI号',
    pattern: '^\\d{15}$',
    description: '匹配手机IMEI号（15位数字）',
    example: '123456789012345'
  },
  {
    name: 'Base64',
    pattern: '^[A-Za-z0-9+/]*={0,2}$',
    description: '匹配Base64编码',
    example: 'SGVsbG8gV29ybGQ='
  },
  {
    name: 'ISBN',
    pattern: '^(?:ISBN(?:-1[03])?:? )?(?=[0-9X]{10}$|(?=(?:[0-9]+[- ]){3})[- 0-9X]{13}$|97[89][0-9]{10}$|(?=(?:[0-9]+[- ]){4})[- 0-9]{17}$)(?:97[89][- ]?)?[0-9]{1,5}[- ]?[0-9]+[- ]?[0-9]+[- ]?[0-9X]$',
    description: '匹配ISBN号码（10位或13位）',
    example: '978-0-13-149505-0'
  },
  {
    name: '中文及标点',
    pattern: '^[\\u4e00-\\u9fa5\\u3000-\\u303f\\uff00-\\uffef]+$',
    description: '匹配中文字符和中文标点符号',
    example: '你好，世界！'
  },
  {
    name: '文件扩展名',
    pattern: '\\.([a-zA-Z0-9]+)$',
    description: '匹配文件扩展名',
    example: '.jpg'
  },
  {
    name: 'Markdown链接',
    pattern: '\\[([^\\]]+)\\]\\(([^\\)]+)\\)',
    description: '匹配Markdown格式的链接',
    example: '[链接文字](https://example.com)'
  },
  {
    name: 'HTML标签',
    pattern: '<([a-z]+)([^<]+)*(?:>(.*))</\\1>|<[a-z]+[^>]*/>',
    description: '匹配HTML标签',
    example: '<div class="example">内容</div>'
  },
  {
    name: '货币金额',
    pattern: '^¥?\\s*\\-?([1-9]\\d{0,2}(,\\d{3})*|0)\\.\\d{2}$',
    description: '匹配货币金额（支持千分位）',
    example: '¥1,234.56'
  },
  {
    name: '中国营业执照',
    pattern: '^[1-9]\\d{14}$',
    description: '匹配15位营业执照注册号',
    example: '123456789012345'
  },
  {
    name: '中国军官证',
    pattern: '^[\\u4e00-\\u9fa5](字第)([0-9a-zA-Z]{4,8})(号?)$',
    description: '匹配中国军官证号码',
    example: '军字第123456号'
  },
  {
    name: '时间戳',
    pattern: '^\\d{13}$',
    description: '匹配13位时间戳',
    example: '1632648372123'
  },
  {
    name: 'Git提交ID',
    pattern: '^[0-9a-f]{40}$',
    description: '匹配Git提交的SHA-1哈希值',
    example: '7b7eb585c0eb6d9cd9d8a3557b50f45f46b2f435'
  },
  {
    name: '中文数字',
    pattern: '^[零一二三四五六七八九十百千万亿]+$',
    description: '匹配中文数字',
    example: '一百二十三'
  },
  {
    name: '端口号',
    pattern: '^([1-9]|[1-5]?[0-9]{2,4}|6[1-4][0-9]{3}|65[1-4][0-9]{2}|655[1-2][0-9]|6553[1-5])$',
    description: '匹配有效端口号（1-65535）',
    example: '8080'
  },
  {
    name: '中国居住证',
    pattern: '^[1-9]\\d{5}[1-2]\\d{3}((0[1-9])|(1[0-2]))((0[1-9])|([1-2][0-9])|(3[0-1]))\\d{3}$',
    description: '匹配中国居住证号码',
    example: '123456200001011234'
  },
  {
    name: '视频链接',
    pattern: '^https?:\\/\\/[\\w\\-\\.]+\\.[a-zA-Z]{2,}\\/?.*\\.(?:mp4|webm|ogg)$',
    description: '匹配视频文件链接',
    example: 'https://example.com/video.mp4'
  },
  {
    name: '图片链接',
    pattern: '^https?:\\/\\/[\\w\\-\\.]+\\.[a-zA-Z]{2,}\\/?.*\\.(?:jpg|jpeg|png|gif|webp)$',
    description: '匹配图片文件链接',
    example: 'https://example.com/image.jpg'
  },
  {
    name: '微信号',
    pattern: '^[a-zA-Z][-_a-zA-Z0-9]{5,19}$',
    description: '匹配微信号（字母开头，6-20位）',
    example: 'wxid_123456'
  }
]

const handleTest = () => {
  matches.value = []
  error.value = ''

  if (!pattern.value || !testText.value) return

  try {
    const regex = new RegExp(pattern.value, flags.value || 'g')
    let match
    let results = []

    if (flags.value?.includes('g')) {
      // 全局匹配模式
      while ((match = regex.exec(testText.value)) !== null) {
        // 克隆匹配结果，因为 exec 会重用同一个数组对象
        let clonedMatch = [...match]
        clonedMatch.index = match.index
        results.push(clonedMatch)
      }
    } else {
      // 非全局匹配模式
      match = testText.value.match(regex)
      if (match) {
        results = [match]
      }
    }

    matches.value = results
  } catch (e) {
    error.value = `正则表达式错误: ${e.message}`
  }
}

const useCommonPattern = (item) => {
  pattern.value = item.pattern
  flags.value = item.flags || 'g'
  handleTest()
}

// 添加输入事件监听
const handleInput = () => {
  handleTest()
}
</script>

<style scoped>
.regex-tester {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.tester-container {
  margin-top: 20px;
}

.section {
  margin: 20px 0;
}

.regex-input {
  display: flex;
  align-items: center;
  gap: 5px;
}

.delimiter {
  font-size: 18px;
  color: #409EFF;
}

.flags-input {
  width: 100px;
}

.flags-info {
  margin: 10px 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.flag-tag {
  margin: 2px;
}

.match-info {
  margin-bottom: 10px;
  color: #409EFF;
}

.match-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.match-item {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  margin-bottom: 10px;
  overflow: hidden;
}

.match-header {
  background-color: #f5f7fa;
  padding: 8px 12px;
  font-weight: bold;
}

.match-content {
  padding: 12px;
}

.match-text {
  color: #409EFF;
  font-family: monospace;
  background-color: #ecf5ff;
  padding: 2px 4px;
  border-radius: 2px;
  word-break: break-all;
}

.error-message {
  color: #f56c6c;
  padding: 10px;
  background-color: #fef0f0;
  border-radius: 4px;
}

.no-matches {
  color: #909399;
  text-align: center;
  padding: 20px;
}

.common-patterns {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .regex-input {
    flex-wrap: wrap;
  }
  
  .flags-input {
    width: 100%;
  }
}
</style> 